import React, { useState, useEffect } from "react";
import { sourcesApi } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Check } from "@phosphor-icons/react";

const AUTHORITY_BADGES = {
  official:   "bg-slate-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded",
  consulting: "border border-violet-400 text-violet-700 text-[10px] font-bold px-1.5 py-0.5 rounded",
  media:      "border border-slate-300 text-slate-600 text-[10px] font-bold px-1.5 py-0.5 rounded",
  industry:   "border border-blue-400 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded",
};

const STATUS_BADGES = {
  active: "text-emerald-700 bg-emerald-50 border border-emerald-200",
  paused: "text-slate-600 bg-slate-100 border border-slate-200",
  error:  "text-rose-700 bg-rose-50 border border-rose-200",
};

export default function SourceMonitorPage() {
  const [sources, setSources]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({
    type: "url", value: "", name: "", authority_tier: "media", industry: "electrification",
  });
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await sourcesApi.list();
      setSources(data);
    } catch { toast.error("Failed to load sources"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.value.trim()) return;
    setSaving(true);
    try {
      const { data } = await sourcesApi.create({ ...form, name: form.name || form.value.slice(0, 60) });
      setSources((s) => [data, ...s]);
      setForm({ type: "url", value: "", name: "", authority_tier: "media", industry: "electrification" });
      setShowForm(false);
      toast.success("Source added");
    } catch { toast.error("Failed to add source"); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (src) => {
    const next = src.feed_status === "active" ? "paused" : "active";
    try {
      const { data } = await sourcesApi.update(src.id, { feed_status: next, active: next === "active" });
      setSources((s) => s.map((x) => x.id === src.id ? data : x));
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this source?")) return;
    try {
      await sourcesApi.delete(id);
      setSources((s) => s.filter((x) => x.id !== id));
      toast.success("Source removed");
    } catch { toast.error("Failed to remove"); }
  };

  const urlSources = sources.filter((s) => s.type !== "rss");
  const rssSources = sources.filter((s) => s.type === "rss");

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
            Source Monitor
          </div>
          <h1 className="text-xl font-bold text-slate-900">
            Sources ({sources.length})
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Every insight traces back here. Add URLs or RSS feeds — the system extracts metadata, statistics, quotes, and authority level.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 px-3 py-2 rounded hover:bg-slate-700 transition-colors"
        >
          <Plus size={13} />
          Add source
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={submit} className="mb-6 border border-slate-200 rounded-md p-5 bg-white space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">
                Type
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              >
                <option value="url">URL</option>
                <option value="rss">RSS Feed</option>
                <option value="keyword">Keyword</option>
                <option value="topic">Topic</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">
                Authority
              </label>
              <select
                value={form.authority_tier}
                onChange={(e) => setForm((f) => ({ ...f, authority_tier: e.target.value }))}
                className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              >
                <option value="official">Official</option>
                <option value="consulting">Consulting</option>
                <option value="industry">Industry</option>
                <option value="media">Media</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">
              URL / Value
            </label>
            <input
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              placeholder={form.type === "url" || form.type === "rss" ? "https://example.com/feed" : "Search keyword or topic"}
              className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">
              Name (optional)
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. RenewEconomy"
              className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <p className="text-[10px] text-slate-400">Authority is auto-classified but can be overridden.</p>
            <button type="button" onClick={() => setShowForm(false)} className="ml-auto text-xs text-slate-500 hover:text-slate-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 px-3 py-1.5 rounded disabled:opacity-40 hover:bg-slate-700 transition-colors"
            >
              <Check size={12} />
              Add source
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-xs text-slate-400 py-12 text-center">Loading sources…</div>
      ) : (
        <div className="space-y-8">
          {/* URL Sources */}
          {urlSources.length > 0 && (
            <div>
              <h2 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">
                URL / Keyword Sources ({urlSources.length})
              </h2>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-4 py-2 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  <span>Source</span>
                  <span>Authority</span>
                  <span>Type</span>
                  <span>Status</span>
                  <span></span>
                </div>
                {urlSources.map((src) => (
                  <div key={src.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-4 py-3 items-center">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{src.name || src.value}</div>
                      {src.name && (
                        <div className="text-[10px] font-mono text-slate-400 truncate mt-0.5">{src.value}</div>
                      )}
                    </div>
                    <span className={AUTHORITY_BADGES[src.authority_tier] || AUTHORITY_BADGES.media}>
                      {(src.authority_tier || "media").toUpperCase()}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500 uppercase">{src.type}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_BADGES[src.feed_status] || STATUS_BADGES.active}`}>
                      {src.feed_status || "active"}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleStatus(src)}
                        className="text-[10px] text-slate-500 hover:text-slate-800 border border-slate-200 px-2 py-0.5 rounded transition-colors"
                      >
                        {src.feed_status === "paused" ? "Resume" : "Pause"}
                      </button>
                      <button
                        onClick={() => remove(src.id)}
                        className="p-1 rounded hover:bg-rose-50 hover:text-rose-600 text-slate-400 transition-colors"
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RSS Feeds */}
          {rssSources.length > 0 && (
            <div>
              <h2 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">
                RSS Feeds ({rssSources.length})
              </h2>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white overflow-hidden">
                {rssSources.map((src) => (
                  <div key={src.id} className="flex items-center gap-4 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{src.name || src.value}</div>
                      <div className="text-[10px] font-mono text-slate-400 truncate mt-0.5">{src.value}</div>
                    </div>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_BADGES[src.feed_status] || STATUS_BADGES.active}`}>
                      {src.feed_status || "active"}
                    </span>
                    <button
                      onClick={() => toggleStatus(src)}
                      className="text-[10px] text-slate-500 hover:text-slate-800 border border-slate-200 px-2 py-0.5 rounded transition-colors"
                    >
                      {src.feed_status === "paused" ? "Resume" : "Pause"}
                    </button>
                    <button onClick={() => remove(src.id)} className="p-1 rounded hover:bg-rose-50 hover:text-rose-600 text-slate-400 transition-colors">
                      <Trash size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sources.length === 0 && (
            <div className="text-center py-16 text-xs text-slate-400">
              No sources configured yet. Add a URL or RSS feed to start tracking intelligence.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
